import React from 'react';
import { useFormContext } from 'react-hook-form';
import { CurrencyCode } from 'further-types/lp/vehicle';
import EqualisationFooter from '../EqualisationFooter';
import { useParams } from 'react-router-dom';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useEqualisationSection } from '../EqualisationContext';
import api from 'lib/trpcClient';
import { EqualisationSection } from 'further-types/lp/equalisation';
import { EqualisationGeneralCapitalCallsFormValues } from './FormWrapper';
import GeneralCapitalCallCard from './GeneralCapitalCallCard';
import { sumBy } from 'lodash';
import EqualisationsTableHeader from 'components/EqualisationElements/EqualisationsTableHeader';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getQueryKey } from '@trpc/react-query';

interface GeneralCapitalCallsContentProps {
  baseCurrency: CurrencyCode;
}

const GeneralCapitalCallsContent: React.FC<GeneralCapitalCallsContentProps> = ({
  baseCurrency,
}) => {
  const { getValues, watch } =
    useFormContext<EqualisationGeneralCapitalCallsFormValues>();
  const generalCapitalCalls = watch('generalCapitalCalls');
  const { previousSection, nextSection, goToNextSection, goToPreviousSection } =
    useEqualisationSection();
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId } = useLayoutContext();
  const equalisationActionAllocationsQueryKey = getQueryKey(
    api.equalisations.getEqualisationGeneralCapitalCalls,
    {
      equalisationId: id ?? '',
      vehicleId: selectedVehicleId ?? '',
    },
  );
  const updateEqualisation = api.equalisations.update.useMutation(
    useHandleTrpcMutation()({
      invalidations: [equalisationActionAllocationsQueryKey],
    }),
  );

  const allocationsAreValid = generalCapitalCalls?.every(
    (generalCapitalCall) => {
      const includedAllocations =
        generalCapitalCall.newInvestorBreakdown.filter(
          (item) => !item.excludeFromAllocation,
        );
      const totalAllocationAmountLcy = sumBy(includedAllocations, (item) =>
        Number(item.calledFundsLcy),
      );
      return (
        Math.abs(totalAllocationAmountLcy - generalCapitalCall.totalAmountLcy) <
        0.01
      );
    },
  );

  const handleNext = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: nextSection ?? EqualisationSection.Summary,
        generalCapitalCalls: getValues('generalCapitalCalls'),
      },
      {
        onSuccess: () => {
          goToNextSection();
        },
      },
    );
  };

  const handlePrevious = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: previousSection ?? EqualisationSection.Commitments,
        generalCapitalCalls: getValues('generalCapitalCalls'),
      },
      {
        onSuccess: () => {
          goToPreviousSection();
        },
      },
    );
  };

  return (
    <>
      <EqualisationsTableHeader
        headers={['Name', 'Date', 'Amount']}
        gridTemplateColumns="60% 20% 20%"
      />
      {generalCapitalCalls?.map((generalCapitalCall, index) => (
        <GeneralCapitalCallCard
          key={index}
          actionIndex={index}
          isLastElement={index === generalCapitalCalls.length - 1}
          generalCapitalCall={generalCapitalCall}
          baseCurrency={baseCurrency}
        />
      ))}
      <EqualisationFooter
        handleNext={handleNext}
        handlePrevious={handlePrevious}
        disabled={!allocationsAreValid}
        loading={updateEqualisation.isPending}
      />
    </>
  );
};

export default GeneralCapitalCallsContent;
